import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import { format } from 'date-fns';
import { ArrowUpDown, ChevronRight, ExternalLink, Search, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Dialog } from '@/components/common/Dialog';
import { ScrollHintList } from '@/components/dashboard/scoring';
import { CloseButton, SortCaret } from '@/components/ds';
import { Issue } from '@/models/Issue';
import { RequestForQuotation } from '@/models/RequestForQuotation';
import {
  deleteRequestForQuote,
  searchRequestForQuoteByIssue,
  searchRequestForQuoteByProperty,
} from '@/services/api/rfqs';

interface RfqHistoryDialogProps {
  open: boolean;
  propertyId: string;
  issues: Issue[];
  onClose: () => void;
  onOpenRFQ: (rfq: RequestForQuotation) => void;
  containedMobile?: boolean;
}

const font = "'Manrope', sans-serif";
const lime = '#c0ff02';

// Matches the C palette + toolbar conventions in the sibling
// InspectionPunchList.tsx / HomeownerHub.tsx (same RFQ feature domain).
const C = {
  bg3: 'var(--tx-glass-panel)',
  border: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-faint)',
  canvas: '#bbb7af',
};

// Matches SEV_COLORS in HomeownerHub.tsx's budget-plan issue rows.
const SEV_COLOR: Record<string, string> = {
  Critical: '#DB5282',
  Urgent: '#FF9A4D',
  Moderate: '#FFF981',
  Low: '#5AE9FF',
  Monitor: '#8b8680',
};

type SortKey = 'title' | 'trade' | 'date';
const SORT_LABELS: Record<SortKey, string> = {
  title: 'Project',
  trade: 'Trade',
  date: 'Date created',
};

// Matches the local useWindowWidth() convention used in InspectionPunchList.tsx
// / HomeownerHub.tsx — this codebase prefers window.innerWidth over useMediaQuery.
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

export function RfqHistoryDialog({
  open,
  propertyId,
  issues,
  onClose,
  onOpenRFQ,
  containedMobile = false,
}: RfqHistoryDialogProps) {
  const [rfqs, setRfqs] = useState<RequestForQuotation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [pendingDeleteRfq, setPendingDeleteRfq] = useState<RequestForQuotation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const m = useWindowWidth() <= 768;

  const confirmDeleteRFQ = async () => {
    if (!pendingDeleteRfq) return;
    setIsDeleting(true);
    try {
      await deleteRequestForQuote(pendingDeleteRfq.id);
      setRfqs((prev) => prev.filter((r) => r.id !== pendingDeleteRfq.id));
      setPendingDeleteRfq(null);
    } catch (err) {
      console.error('Failed to delete RFQ', err);
      setError("Couldn't delete that RFQ. Try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const issueById = useMemo(
    () => new Map(issues.map((issue) => [issue.id, issue])),
    [issues],
  );

  const loadRfqsFromIssues = async () => {
    const results = await Promise.all(
      issues.map((issue) =>
        searchRequestForQuoteByIssue(issue.id).catch((err) => {
          console.warn(`Failed to load RFQs for issue ${issue.id}`, err);
          return [];
        }),
      ),
    );
    const byId = new Map<string, RequestForQuotation>();
    results.flat().forEach((rfq) => byId.set(rfq.id, rfq));
    return Array.from(byId.values()).sort((a, b) => b.createdAt - a.createdAt);
  };

  useEffect(() => {
    if (!open || !propertyId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    searchRequestForQuoteByProperty(propertyId)
      .then((nextRfqs) => {
        if (!cancelled) setRfqs(nextRfqs);
      })
      .catch(async (err) => {
        console.error('Failed to load RFQ history', err);
        const fallbackRfqs = await loadRfqsFromIssues();
        if (!cancelled) {
          setRfqs(fallbackRfqs);
          if (fallbackRfqs.length === 0) setError("Couldn't load RFQ history.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [issues, open, propertyId]);

  // Reset transient UI state each time the dialog opens fresh.
  useEffect(() => {
    if (open) {
      setSearch('');
      setExpandedIds(new Set());
    }
  }, [open]);

  const handleOpenRFQ = (rfq: RequestForQuotation) => {
    onOpenRFQ(rfq);
    onClose();
  };

  const rows = useMemo(() => {
    const withIssue = rfqs.map((rfq) => ({
      rfq,
      issue: rfq.issueId ? issueById.get(rfq.issueId) : undefined,
    }));

    const q = search.trim().toLowerCase();
    const filtered = q
      ? withIssue.filter(({ rfq, issue }) => {
          const trade = issue?.trade ?? issue?.componentCategory ?? issue?.category ?? '';
          return (
            rfq.projectTitle.toLowerCase().includes(q) ||
            trade.toLowerCase().includes(q) ||
            (issue?.title.toLowerCase().includes(q) ?? false)
          );
        })
      : withIssue;

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'date') return (a.rfq.createdAt - b.rfq.createdAt) * dir;
      if (sortKey === 'title') return a.rfq.projectTitle.localeCompare(b.rfq.projectTitle) * dir;
      const tradeA = a.issue?.trade ?? a.issue?.componentCategory ?? a.issue?.category ?? '';
      const tradeB = b.issue?.trade ?? b.issue?.componentCategory ?? b.issue?.category ?? '';
      return tradeA.localeCompare(tradeB) * dir;
    });
  }, [rfqs, issueById, search, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
  };

  const allExpanded = rows.length > 0 && rows.every(({ rfq }) => expandedIds.has(rfq.id));
  const toggleExpandAll = () =>
    setExpandedIds(allExpanded ? new Set() : new Set(rows.map(({ rfq }) => rfq.id)));
  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const contained = containedMobile && m;
  const historyContent = (
      <Box
        sx={{
          p: m ? '16px' : '25px',
          ...(contained && {
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          }),
        }}>
        {/* Header */}
        <Typography
          sx={{ fontFamily: font, fontSize: 17, fontWeight: 600, color: 'var(--tx-text)', pr: '32px', mb: '6px' }}>
          RFQ History
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: 13, color: 'var(--tx-muted)', mb: '18px' }}>
          Reopen requests generated for this property.
        </Typography>

        {/* Toolbar: search + sort + expand/collapse all — matches the Sort/Filter/
            expand-all toolbar pattern in the sibling InspectionPunchList.tsx. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '14px', flexWrap: m ? 'nowrap' : 'wrap' }}>
          <Box
            sx={{
              flex: 1,
              minWidth: m ? 0 : 180,
              height: '40px',
              background: 'rgba(var(--tx-ink-rgb), 0.05)',
              border: 'none',
              borderRadius: '12px',
              padding: '0 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxSizing: 'border-box',
            }}>
            <Search size={16} color="#eae6de" strokeWidth={2} style={{ flexShrink: 0 }} />
            <Box
              component="input"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              placeholder="Search RFQ history…"
              sx={{
                background: 'none',
                border: 'none',
                outline: 'none',
                fontSize: 12,
                fontFamily: font,
                color: C.text,
                flex: 1,
                '&::placeholder': { color: '#cfcac2' },
              }}
            />
          </Box>

          {m ? (
            <Box
              component="button"
              onClick={(e: React.MouseEvent<HTMLElement>) => setSortMenuAnchor(e.currentTarget)}
              title="Sort"
              aria-label="Sort"
              sx={{
                background: 'rgba(var(--tx-ink-rgb), 0.05)',
                border: 'none',
                borderRadius: '9px',
                color: 'var(--tx-text)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                flexShrink: 0,
                transition: 'color 0.15s, background-color 0.15s',
                '&:hover': { color: C.text, background: '#262626' },
              }}>
              <ArrowUpDown size={16} />
            </Box>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setSortMenuAnchor(e.currentTarget)}
              startIcon={<ArrowUpDown size={14} />}
              sx={{
                bgcolor: 'rgba(var(--tx-ink-rgb), 0.05)',
                border: 'none',
                color: 'var(--tx-text)',
                textTransform: 'none',
                fontFamily: font,
                fontWeight: 600,
                fontSize: 13,
                height: '40px',
                borderRadius: '9px',
                px: '20px',
                flexShrink: 0,
                transition: 'color 0.15s, background-color 0.15s',
                '&:hover': { bgcolor: '#262626', color: C.text, boxShadow: 'none' },
              }}>
              Sort
            </Button>
          )}

          <Box
            component="button"
            onClick={toggleExpandAll}
            title={allExpanded ? 'Collapse all' : 'Expand all'}
            aria-label={allExpanded ? 'Collapse all' : 'Expand all'}
            sx={{
              background: 'rgba(var(--tx-ink-rgb), 0.05)',
              border: 'none',
              borderRadius: '9px',
              color: 'var(--tx-text)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              flexShrink: 0,
              transition: 'color 0.15s, background-color 0.15s',
              '&:hover': { color: C.text, background: '#262626' },
            }}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              {allExpanded ? (
                <>
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="10" y1="14" x2="3" y2="21" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                </>
              ) : (
                <>
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </>
              )}
            </svg>
          </Box>
        </Box>

        <Menu
          anchorEl={sortMenuAnchor}
          open={Boolean(sortMenuAnchor)}
          onClose={() => setSortMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                bgcolor: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                borderRadius: '16px',
                minWidth: 200,
                mt: 1,
                pt: '20px',
                px: '20px',
                pb: '14px',
                boxShadow: 'var(--tx-shadow)',
              },
            },
          }}
          sx={{ '& .MuiMenuItem-root': { borderBottom: 'none' }, '& .MuiList-root': { p: 0 } }}>
          <Typography
            variant="caption"
            sx={{
              color: C.canvas,
              px: '12px',
              pb: '20px',
              display: 'block',
              fontFamily: font,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.3em',
            }}>
            SORT
          </Typography>
          {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
            <MenuItem
              key={key}
              onClick={() => {
                toggleSort(key);
                setSortMenuAnchor(null);
              }}
              selected={sortKey === key}
              sx={{
                color: sortKey === key ? 'var(--tx-text)' : C.canvas,
                bgcolor: sortKey === key ? 'var(--tx-fill-2)' : 'transparent',
                fontFamily: font,
                fontSize: 14,
                fontWeight: sortKey === key ? 600 : 400,
                borderRadius: '8px',
                mx: 0,
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '6px !important',
                paddingBottom: '6px !important',
                paddingLeft: '12px !important',
                paddingRight: '12px !important',
                lineHeight: '1.4 !important',
                '&.Mui-selected': { bgcolor: '#BBB7AF', color: '#262626', '&:hover': { bgcolor: '#BBB7AF' } },
                '&:hover': { bgcolor: sortKey === key ? '#BBB7AF' : 'rgba(255,255,255,0.06)' },
              }}>
              {SORT_LABELS[key]}
              <SortCaret active={sortKey === key} dir={sortDir} />
            </MenuItem>
          ))}
        </Menu>

        <Box>
          {isLoading && (
            <Box
              sx={{
                height: '404px',
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                borderRadius: '16px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <CircularProgress size={24} sx={{ color: lime }} />
            </Box>
          )}

          {!isLoading && error && (
            <Box
              sx={{
                height: '404px',
                p: '20px',
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography sx={{ fontFamily: font, color: 'var(--tx-muted)', fontSize: 14 }}>
                {error}
              </Typography>
            </Box>
          )}

          {!isLoading && !error && rows.length === 0 && (
            <Box
              sx={{
                height: '404px',
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}>
              <Typography sx={{ fontFamily: font, color: 'var(--tx-text)', fontSize: 15, fontWeight: 600 }}>
                {search ? 'No matching RFQs' : 'No RFQs yet'}
              </Typography>
              <Typography sx={{ fontFamily: font, color: 'var(--tx-faint)', fontSize: 13, mt: '6px' }}>
                {search
                  ? 'Try a different search term.'
                  : 'Created RFQs will appear here for recall and follow-up.'}
              </Typography>
            </Box>
          )}

          {!isLoading && !error && rows.length > 0 && (
            <Box sx={{ background: '#1c1c1c', borderRadius: '16px', overflow: 'hidden' }}>
              {/* Column headers — matches the InspectionPunchList.tsx grid-table convention.
                  Skipped on mobile: rows switch to a stacked card layout with no columns
                  to label, and sorting is still reachable via the toolbar Sort button. */}
              {!m && (
                <Box
                  sx={{
                    display: 'grid',
                    // Open shrank from a text button to a small icon — give the
                    // freed width to Trade/Date instead of letting it collapse
                    // into the Project column via 1fr.
                    gridTemplateColumns: '24px 1fr 160px 190px 50px',
                    columnGap: '16px',
                    padding: '8px 14px',
                    borderBottom: '1px solid var(--tx-line)',
                    background: 'var(--tx-glass-panel)',
                    backgroundImage: 'var(--tx-surface-light)',
                    boxShadow: 'var(--tx-shadow)',
                  }}>
                  <Box />
                  {(['title', 'trade', 'date'] as const).map((key) => (
                    <Box
                      key={key}
                      component="button"
                      onClick={() => toggleSort(key)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        background: 'none',
                        border: 'none',
                        p: 0,
                        pl: key === 'date' ? '10px' : 0,
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontFamily: font,
                        fontSize: 10,
                        fontWeight: 600,
                        color: sortKey === key ? 'var(--tx-muted)' : 'var(--tx-faint)',
                      }}>
                      {SORT_LABELS[key]}
                      <SortCaret active={sortKey === key} dir={sortDir} />
                    </Box>
                  ))}
                  <Box />
                </Box>
              )}

              {/* Fixed height (not maxHeight) so the card — and dialog — never resize
                  as search narrows/widens the result count; scrolls internally instead. */}
              {/* Scrollbar hidden entirely (not just styled) — an appearing/disappearing
                  native scrollbar changes the available width and visibly shifts every
                  fixed-width column (Trade/Date/Open) when a row expands past 360px.
                  Matches InspectionPunchList.tsx's .ipl-scroll convention. */}
              <style>{'.rfq-history-scroll::-webkit-scrollbar{width:0;background:transparent}.rfq-history-scroll{scrollbar-width:none}'}</style>
              <ScrollHintList
                className="rfq-history-scroll"
                style={
                  m
                    ? // Mobile has no fixed dialog height to size against — cap the
                      // list to the dynamic viewport (not vh) so header/toolbar/footer
                      // can't get pushed off-screen, matching InspectionPunchList's
                      // mobile card height convention.
                      {
                        maxHeight: contained ? 'calc(100dvh - 360px)' : 'calc(100dvh - 280px)',
                        overflowY: 'auto',
                      }
                    : { height: '360px', overflowY: 'auto' }
                }>
              {rows.map(({ rfq, issue }) => {
                const trade = issue?.trade ?? issue?.componentCategory ?? issue?.category;
                const createdAt = format(new Date(rfq.createdAt), 'MMM d, yyyy h:mm a');
                const isExpanded = expandedIds.has(rfq.id);
                const description = issue?.description?.trim();
                const hasDescription = !!description;
                const sevColor = issue ? (SEV_COLOR[issue.urgency] ?? '#8b8680') : '#8b8680';

                // Mobile: the fixed-width grid columns (Trade/Date/Open) don't fit a
                // narrow viewport — stack each RFQ into a title+open line, then a
                // trade+date+chevron line, matching InspectionPunchList's mobile card
                // pattern instead of squeezing a desktop table into less width.
                if (m) {
                  return (
                    <Box
                      key={rfq.id}
                      onClick={() => toggleExpand(rfq.id)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        padding: '12px 14px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--tx-line)',
                      }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <Box
                          sx={{
                            width: 3,
                            height: '32px',
                            borderRadius: '2px',
                            background: sevColor,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            flex: 1,
                            fontFamily: font,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--tx-text)',
                            lineHeight: 1.4,
                          }}>
                          {rfq.projectTitle}
                        </Typography>
                        <Box
                          component="button"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleOpenRFQ(rfq);
                          }}
                          aria-label="Open RFQ"
                          title="Open RFQ"
                          // Icon-only "open" CTA — an "Open" text label reads as an issue
                          // status elsewhere (InspectionPunchList's Open/Done badge), so
                          // this stays an unambiguous action icon across the whole app.
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '28px',
                            height: '28px',
                            background: 'rgba(var(--tx-ink-rgb), 0.05)',
                            border: 'none',
                            borderRadius: '9px',
                            color: 'var(--tx-muted)',
                            cursor: 'pointer',
                            flexShrink: 0,
                            p: 0,
                            transition: 'color 0.15s, background-color 0.15s',
                            '&:hover': { background: '#262626', color: '#f3f3f3' },
                          }}>
                          <ExternalLink size={14} />
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', pl: '13px' }}>
                        <Typography
                          sx={{
                            fontFamily: font,
                            fontSize: 10,
                            color: 'var(--tx-muted)',
                          }}>
                          {trade || '—'}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography sx={{ fontFamily: font, fontSize: 10, color: '#6e6b66' }}>
                          {createdAt}
                        </Typography>
                        <ChevronRight
                          size={14}
                          style={{
                            color: 'var(--tx-faint)',
                            transform: isExpanded ? 'rotate(90deg)' : 'none',
                            transition: 'transform 0.15s',
                          }}
                        />
                      </Box>

                      {isExpanded && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', pl: '13px', pt: '4px' }}>
                          {hasDescription && (
                            <Typography sx={{ fontFamily: font, fontSize: 12, color: 'var(--tx-muted)', lineHeight: 1.55 }}>
                              {description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Box
                              component="button"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                setPendingDeleteRfq(rfq);
                              }}
                              aria-label="Delete RFQ"
                              title="Delete RFQ"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '26px',
                                height: '26px',
                                background: 'none',
                                border: 'none',
                                p: 0,
                                cursor: 'pointer',
                                color: 'var(--tx-faint)',
                                borderRadius: '6px',
                                transition: 'color 0.15s, background-color 0.15s',
                                '&:hover': { color: '#DB5282', background: 'rgba(219,82,130,0.1)' },
                              }}>
                              <Trash2 size={13} />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                }

                return (
                  <Box
                    key={rfq.id}
                    onClick={() => toggleExpand(rfq.id)}
                    sx={{
                      display: 'grid',
                      // Open shrank from a text button to a small icon — give the
                    // freed width to Trade/Date instead of letting it collapse
                    // into the Project column via 1fr.
                    gridTemplateColumns: '24px 1fr 160px 190px 50px',
                      columnGap: '16px',
                      padding: '10px 14px',
                      alignItems: 'start',
                      cursor: 'pointer',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '24px' }}>
                      <Box
                        component="button"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          toggleExpand(rfq.id);
                        }}
                        aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '22px',
                          height: '22px',
                          p: 0,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--tx-faint)',
                          flexShrink: 0,
                        }}>
                        <ChevronRight
                          size={16}
                          style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, height: '24px' }}>
                      <Box sx={{ width: 3, height: 20, borderRadius: '2px', background: sevColor, flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--tx-text)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                          flex: 1,
                        }}>
                        {rfq.projectTitle}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 11,
                        color: 'var(--tx-muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                      {trade || '—'}
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 11,
                        color: 'var(--tx-faint)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        pl: '10px',
                      }}>
                      {createdAt}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '24px' }}>
                      <Box
                        component="button"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          handleOpenRFQ(rfq);
                        }}
                        aria-label="Open RFQ"
                        title="Open RFQ"
                        // Icon-only "open" CTA — an "Open" text label reads as an issue
                        // status elsewhere (InspectionPunchList's Open/Done badge), so
                        // this stays an unambiguous action icon across the whole app.
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '28px',
                          height: '28px',
                          background: 'rgba(var(--tx-ink-rgb), 0.05)',
                          border: 'none',
                          borderRadius: '9px',
                          color: 'var(--tx-muted)',
                          cursor: 'pointer',
                          p: 0,
                          transition: 'color 0.15s, background-color 0.15s',
                          '&:hover': { background: '#262626', color: '#f3f3f3' },
                        }}>
                        <ExternalLink size={14} />
                      </Box>
                    </Box>

                    {isExpanded && (
                      <>
                        {hasDescription && (
                          <Typography
                            sx={{
                              gridColumn: '2 / span 2',
                              fontFamily: font,
                              fontSize: 12,
                              color: 'var(--tx-muted)',
                              lineHeight: 1.55,
                              pt: '8px',
                              pl: '13px',
                            }}>
                              {description}
                          </Typography>
                        )}
                        <Box
                          sx={{
                            gridColumn: '5 / 6',
                            alignSelf: 'stretch',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-end',
                            pb: '2px',
                          }}>
                          <Box
                            component="button"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setPendingDeleteRfq(rfq);
                            }}
                            aria-label="Delete RFQ"
                            title="Delete RFQ"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'flex-end',
                              justifyContent: 'center',
                              width: '26px',
                              height: '18px',
                              background: 'none',
                              border: 'none',
                              p: 0,
                              cursor: 'pointer',
                              color: 'var(--tx-faint)',
                              borderRadius: '6px',
                              transition: 'color 0.15s, background-color 0.15s',
                              '&:hover': { color: '#DB5282', background: 'rgba(219,82,130,0.1)' },
                            }}>
                            <Trash2 size={13} />
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })}
              </ScrollHintList>

              {/* Footer count — matches InspectionPunchList.tsx's convention */}
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 11,
                  color: 'var(--tx-faint)',
                  mt: '4px',
                  pt: '12px',
                  pb: '14px',
                  px: '14px',
                  borderTop: '1px solid var(--tx-line)',
                }}>
                Showing {rows.length} of {rfqs.length}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
  );

  return (
    <>
    {contained && open ? (
      <Box
        data-testid="rfq-history-dialog"
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 20,
          background: '#222',
          border: 0,
          borderRadius: '25px',
          overflow: 'hidden',
          boxShadow: '0 18px 48px rgba(0,0,0,0.34)',
        }}>
        <CloseButton
          onClose={onClose}
          label="Close RFQ history"
          sx={{ position: 'absolute', top: 18, right: 18, zIndex: 2 }}
        />
        {historyContent}
      </Box>
    ) : !contained ? (
      <Dialog
        open={open}
        onClose={onClose}
        width={m ? undefined : 860}
        minHeight={m ? undefined : 600}
        containerProps={{ gap: 0, p: 0 }}
        data-testid="rfq-history-dialog"
        // Matches NegotiationStrategyDialog's lighter, warmer glass treatment —
        // the shared Dialog's default is darker/near-opaque, which the blur
        // barely reads against; this makes the two feel like the same surface.
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'rgba(187,183,175,0.1) !important',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              // Matches AdvisorClientsPage's mobile Dialog precedent — the fixed
              // 860px desktop width otherwise forces the internal grid table to
              // overflow/collapse on narrow viewports. Explicit my + dvh maxHeight
              // (not just the list's own internal cap) so the card's total height
              // is guaranteed to fit the visible viewport with a small inset
              // margin, same discipline as ReviewAndShareRFQDialog.
              ...(m && {
                width: 'calc(100vw - 20px)',
                maxWidth: 'calc(100vw - 20px) !important',
                mx: '10px',
                my: '10px',
                maxHeight: 'calc(100dvh - 20px)',
              }),
            },
          },
          backdrop: {
            sx: { backgroundColor: 'rgba(20,20,20,0.35)' },
          },
        }}>
        {historyContent}
      </Dialog>
    ) : null}

    <Dialog
      open={!!pendingDeleteRfq}
      onClose={() => setPendingDeleteRfq(null)}
      disableClose={isDeleting}
      width={380}
      containerProps={{ gap: 0, p: 0 }}
      data-testid="rfq-delete-confirm-dialog"
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'rgba(38,38,38,0.95) !important',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          },
        },
        backdrop: { sx: { backgroundColor: 'rgba(20,20,20,0.5)' } },
      }}>
      <Box sx={{ p: '25px' }}>
        <Typography sx={{ fontFamily: font, fontSize: 17, fontWeight: 600, color: 'var(--tx-text)', pr: '32px', mb: '8px' }}>
          Delete this RFQ?
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: 13, color: 'var(--tx-muted)', lineHeight: 1.5, mb: '22px' }}>
          {pendingDeleteRfq
            ? `“${pendingDeleteRfq.projectTitle}” will be permanently removed. This can’t be undone.`
            : "This can't be undone."}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Button
            onClick={() => setPendingDeleteRfq(null)}
            disabled={isDeleting}
            sx={{
              height: '40px',
              px: '20px',
              borderRadius: '12px',
              fontFamily: font,
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: '-0.02em',
              textTransform: 'none',
              color: 'var(--tx-muted)',
              background: 'rgba(var(--tx-ink-rgb), 0.05)',
              border: 'none',
              '&:hover': { background: '#262626', color: '#f3f3f3', boxShadow: 'none' },
            }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteRFQ}
            disabled={isDeleting}
            sx={{
              height: '40px',
              px: '20px',
              borderRadius: '10px',
              fontFamily: font,
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: '-0.02em',
              textTransform: 'none',
              color: '#fff',
              background: '#DB5282',
              border: 'none',
              boxShadow: 'none',
              minWidth: '112px',
              '&:hover': { background: '#c73f70', boxShadow: 'none' },
              '&.Mui-disabled': { background: '#DB5282', opacity: 0.6, color: '#fff' },
            }}>
            {isDeleting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Delete RFQ'}
          </Button>
        </Box>
      </Box>
    </Dialog>
    </>
  );
}
